import { alpha, tabsClasses } from "@mui/material"
import { Spacing } from "@mui/system"
import { blueGrey, lightBlue } from "../../../../snapcourse/themes/palette"
import { CSSObject } from "tss-react"
import { makeStyles } from "tss-react/mui"
import { NavPillsMode, NavPillsSize, NavPillsVariant } from "./types"

const getTabBackgroundColor = (
  mode: NavPillsMode,
  variant: NavPillsVariant
): string => {
  switch (variant) {
    case "text":
      return "transparent"
    case "contained":
      return alpha(mode === "light" ? blueGrey[50] : blueGrey[600], 0.5)
    default:
      return "transparent"
  }
}

const getTabSizeProperties = (
  size: NavPillsSize,
  spacing: Spacing
): CSSObject => {
  const defaultProperties: CSSObject = {
    fontSize: "0.8125rem",
    minHeight: "36px"
  }
  switch (size) {
    case "sm":
      return {
        fontSize: "0.75rem",
        minHeight: "30px",
        "& .material-icons": {
          fontSize: "0.6875rem"
        }
      }
    case "md":
      return defaultProperties
    case "lg":
      return {
        fontSize: "0.875rem",
        minHeight: "42px",
        padding: spacing(0, 5)
      }
    default:
      return defaultProperties
  }
}

export const useStyles = makeStyles<
  {
    size: NavPillsSize
    mode: NavPillsMode
    variant: NavPillsVariant
  },
  | "tabRoot"
  | "tabIndicator"
  | "disableTabIndicator"
  | "tabsRoot"
  | "tabScroller"
  | "tabsFlexContainer"
>()(({ palette, spacing, mixins }, { size, mode, variant }, classes) => ({
  tabRoot: {
    gap: spacing(2),
    padding: spacing(0, 4),
    borderRadius: variant === "underlined" ? 0 : 50,
    border: `1px solid ${
      variant === "outlined"
        ? mode === "light"
          ? blueGrey[100]
          : blueGrey[600]
        : "transparent"
    }`,
    color: mode === "light" ? blueGrey[700] : palette.common.white,
    backgroundColor: getTabBackgroundColor(mode, variant),

    "&:focus": {
      // a11y: white box-shadow ring on dark surfaces — blue fails 3:1 contrast (WCAG 1.4.11)
      ...(mode === "light"
        ? {
            ...mixins.focusRing.outline,
            // a11y: !important so the focus ring wins the equal-specificity tie with .Mui-selected
            borderColor: `${mixins.focusRing.outline.borderColor} !important`,
            outline: `${mixins.focusRing.outline.outline} !important`
          }
        : mixins.focusRingOnDark.ring)
    },
    "&:hover": {
      background: mode === "light" ? blueGrey[50] : blueGrey[600],
      textDecoration: "none",
      color: mode === "light" ? blueGrey[700] : palette.common.white
    },
    "&.Mui-selected": {
      color: mode === "light" ? lightBlue[900] : palette.common.white,
      backgroundColor:
        variant !== "underlined"
          ? mode === "light"
            ? lightBlue[50]
            : lightBlue[600]
          : "transparent",
      borderColor:
        variant === "outlined"
          ? mode === "light"
            ? lightBlue[50]
            : lightBlue[600]
          : "transparent"
    },
    "&.Mui-disabled": {
      color: palette.primary[200]
    },

    "& .material-icons": {
      color: mode === "light" ? lightBlue[500] : palette.common.white
    },

    ...getTabSizeProperties(size, spacing)
  },
  tabIndicator:
    variant !== "underlined"
      ? {
          top: "2px",
          height: size === "lg" ? "91%" : size === "md" ? "90%" : "88%",
          zIndex: -1,
          backgroundColor: mode === "light" ? lightBlue[50] : lightBlue[600],
          borderRadius: 100,
          outline: "none"
        }
      : {
          height: "3px",
          bottom: "2px",
          borderRadius: "3px",
          backgroundColor: lightBlue[500]
        },
  disableTabIndicator: {
    display: "none"
  },
  tabsRoot: {
    display: "flex",
    alignItems: "center",
    minHeight: "auto",
    "& .MuiTabScrollButton-root.Mui-disabled": {
      display: "none"
    },
    "& .MuiTabs-fixed": {
      overflow: "unset !important"
    },

    [`&.${tabsClasses.vertical} `]: {
      [`
      .${classes.tabScroller},
      .${classes.tabsFlexContainer},
      .${classes.tabRoot},
      .${classes.tabIndicator}
      `]: {
        width: "100%"
      }
    }
  },
  tabScroller: {
    height: "100%",
    display: "flex",
    alignItems: "center"
  },
  tabsFlexContainer: {
    padding: "2px",
    display: "flex",
    alignItems: "center",
    height: "100%",
    gap: spacing(2)
  }
}))
